import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SupportApiChat } from '../support-system.models';
import { SupportInboxService } from './support-inbox.service';

type Permission = NotificationPermission | 'unsupported';

/**
 * Keeps the team inbox running on every dashboard page (started by the
 * sidenav for `behamics_support_chat` holders):
 * - `waitingCount` for the badge on the sidenav's Support link;
 * - a snackbar for each new client message while on another page, or a
 *   desktop notification while the dashboard tab is in the background.
 */
@Injectable({ providedIn: 'root' })
export class SupportInboxAlertsService {
  private inbox = inject(SupportInboxService);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private zone = inject(NgZone);

  readonly waitingCount = computed(() =>
    this.inbox.access() === 'ok' ? this.inbox.waitingCount() : 0,
  );
  readonly permission = signal<Permission>(
    'Notification' in window ? Notification.permission : 'unsupported',
  );

  private subscription: Subscription | null = null;

  /** Idempotent. */
  start(): void {
    if (this.subscription) return;
    this.inbox.init();
    this.subscription = this.inbox.incoming$.subscribe((chat) => this.notify(chat));
  }

  /** Logout or lost privilege: stop listening and forget the inbox. */
  stop(): void {
    if (!this.subscription) return;
    this.subscription.unsubscribe();
    this.subscription = null;
    this.inbox.destroy();
  }

  /** Browsers only allow asking from a click. */
  requestPermission(): void {
    if (this.permission() !== 'default') return;
    Notification.requestPermission().then((result) =>
      this.zone.run(() => this.permission.set(result)),
    );
  }

  open(chat: SupportApiChat): void {
    this.router.navigate(['/support-inbox'], { queryParams: { chat: chat._id } });
  }

  private notify(chat: SupportApiChat): void {
    const onInbox = this.router.url.startsWith('/support-inbox');
    const away = document.hidden || !document.hasFocus();
    if (onInbox && !away && this.inbox.selectedId() === chat._id) return;

    // Only say that something arrived: no client name or message text, which
    // could be read over a shoulder or on a lock screen. A new report says so
    // (a stop-nudge request is urgent); later messages in it are plain messages.
    const newReport = chat.type === 'issue' && chat.messageCount <= 1;
    const title = !newReport
      ? 'New support message'
      : chat.request?.type === 'stop_nudges'
        ? 'New stop-nudge request'
        : 'New issue report';

    if (away && this.permission() === 'granted') {
      const notification = new Notification(title, {
        body: 'Open the support inbox to read it.',
        tag: `support-chat-${chat._id}`,
        icon: 'assets/img/favicon.png',
      });
      notification.onclick = () =>
        this.zone.run(() => {
          window.focus();
          this.open(chat);
          notification.close();
        });
      return;
    }
    // On the inbox the list already shows it.
    if (onInbox) return;
    this.snackBar
      .open(title, 'Open', {
        duration: 7000,
        panelClass: 'snackbar-class-success',
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
      })
      .onAction()
      .subscribe(() => this.open(chat));
  }
}
